// Everything the quote drawer reads besides the quote itself (#1559). Every
// value tolerates a failed or empty read: a list comes back empty, a setting
// falls back, and the drawer's figures never depend on these (they read
// quotes.amount_paid).
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { QuoteRow } from "@/hooks/useQuotes";
import { useEventDetail } from "@/hooks/useEvents";
import { useContactMessages } from "@/hooks/useContactMessages";
import { useAppSettings } from "@/hooks/useAppSettings";
import { useDraftDeletable } from "@/hooks/useEventsPipeline";
import { useMenuItems } from "@/hooks/useMenuItems";
import { useDietaryTags } from "@/hooks/useDietaryTags";
import { gratuityDefault } from "./drawerPayload";
import type { QuoteResponse } from "./drawerLogic";

export interface QuotePaymentRow {
  id: string;
  quote_id: string;
  amount: number;
  fee_amount?: number | null;
  method: string;
  reference: string | null;
  notes: string | null;
  recorded_by: string | null;
  payment_date: string;
  created_at: string;
  recorded_by_profile?: { display_name: string | null } | null;
}

export type QuoteResponseRow = QuoteResponse & { id: string };

/** The old panel's payment history read: same select, same key. */
function useQuotePayments(quoteId: string) {
  return useQuery({
    queryKey: ["quote_payments", quoteId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("quote_payments")
        .select("*, recorded_by_profile:profiles!quote_payments_recorded_by_fkey(display_name)")
        .eq("quote_id", quoteId)
        .order("payment_date", { ascending: false });
      if (error) throw error;
      return (data ?? []) as unknown as QuotePaymentRow[];
    },
  });
}

/** The old panel's client responses read: same select, same key. */
function useQuoteResponses(quoteId: string) {
  return useQuery({
    queryKey: ["quote_responses", quoteId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("quote_responses")
        .select("*")
        .eq("quote_id", quoteId)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as unknown as QuoteResponseRow[];
    },
  });
}

/** A brand default percent from app_settings, or undefined when unset or not positive. */
function settingPct(settings: Record<string, string>, key: string): number | undefined {
  const pct = parseFloat(settings[key] ?? "");
  return Number.isFinite(pct) && pct > 0 ? pct : undefined;
}

/** Dietary tag labels per menu item, for the view rows and the add bar. */
function useDietaryLabels() {
  const menu = useMenuItems();
  const tags = useDietaryTags();
  const labelFor = new Map((tags.data ?? []).map((t) => [t.key, t.label]));
  const byItem = new Map((menu.data ?? []).map((m) => [m.id, m.dietary_tags ?? []]));
  return {
    menuItems: menu.data ?? [],
    dietaryLabels: (menuItemId: string | null | undefined): string[] =>
      menuItemId ? (byItem.get(menuItemId) ?? []).map((k) => labelFor.get(k) ?? k) : [],
  };
}

export function useQuoteDrawerData(quote: QuoteRow) {
  const payments = useQuotePayments(quote.id);
  const responses = useQuoteResponses(quote.id);
  const event = useEventDetail(quote.event_id ?? null);
  const messages = useContactMessages(quote.contact_id);
  const settingsQuery = useAppSettings();
  const deletable = useDraftDeletable(quote.status === "draft" ? quote : null);
  const { menuItems, dietaryLabels } = useDietaryLabels();
  const settings = settingsQuery.data ?? {};
  return {
    payments: payments.data ?? [],
    paymentsError: payments.isError,
    paymentsLoading: payments.isLoading,
    responses: responses.data ?? [],
    event: event.data ?? null,
    eventError: event.isError,
    messages: messages.data ?? [],
    lastMessage: messages.data?.[0] ?? null,
    // For emailSwitchFrom (the quote-sent toast) and the Settings-dependent guards.
    settingsQuery,
    etransferEmail: settings.etransfer_email?.trim() || null,
    defaultDepositPct: settingPct(settings, "default_deposit_percent"),
    defaultGratuityPct: gratuityDefault(settings),
    // undefined while loading or failed: Delete draft re-checks on confirm anyway.
    deletable: deletable.isSuccess ? deletable.data : undefined,
    menuItems,
    dietaryLabels,
  };
}
